import React, { useState } from 'react'
import './ProviderSidePanel.css'
import { Button as Btn, ButtonGroup } from 'react-bootstrap'
import { Tooltip } from '@enamelai/design-system'
import Details from './Details.component'
import History from './History.component'
import WorkShift from './WorkShift.component'
import { getFullName } from '@utils/fullname.util'
import { Provider } from '@dataTypes/provider.types'
import { EmploymentStatus } from '@constants/Providers'
import { useProviderActions } from '../hooks/useProviderActions'
import SelectProviderContactModal from '@components/layout/Modals/SelectProviderContact/SelectProviderContactModal.component'
import AddProviderModal from '@components/layout/Modals/AddProviderModal/AddProviderModal.component'
import ConfirmDeleteModal from '@components/layout/Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import EmailSender from '@components/layout/Modals/EmailSender/EmailSender.component'
import EditPhoto from '@components/layout/Modals/EditPhoto/EditPhoto.component'
import { mapDatabaseToProviderData } from '@utils/provider.mapper'
import Avatar from '@components/common/Avatar/Avatar.component'
import defaultAvatar from '@assets/images/default_avatar.png'
import { ReactComponent as PanelCloseIcon } from '@assets/images/Sidepanel/panel-close.svg'

type SidePanelProps = {
  provider: Provider | null
  isHidden: boolean
  toggle: () => void
}

enum Tabs {
  DETAILS = 'details',
  HISTORY = 'history',
  WORK_SHIFT = 'work-shift',
}

const listofTabs = [
  {
    name: Tabs.DETAILS,
    unselected: 'mgc_IDcard_line',
    selected: 'mgc_IDcard_fill',
    tooltip: 'Details',
  },
  {
    name: Tabs.HISTORY,
    unselected: 'mgc_history_anticlockwise_line',
    selected: 'mgc_history_anticlockwise_fill',
    tooltip: 'Appointments',
  },
  {
    name: Tabs.WORK_SHIFT,
    unselected: 'mgc_calendar_time_add_line',
    selected: 'mgc_calendar_time_add_fill',
    tooltip: 'Work schedule',
  },
]

const ProviderSidePanel: React.FC<SidePanelProps> = ({
  provider,
  isHidden,
  toggle,
}: SidePanelProps): React.ReactElement => {
  const [selectedTab, setSelectedTab] = useState<Tabs>(Tabs.DETAILS)
  const [showEditPhotoModal, setShowEditPhotoModal] = useState(false)
  const {
    handleCallClick,
    handleEmailClick,
    handleMessageClick,
    confirmDeleteProvider,
    handleCloseCallModal,
    handleCloseEmailSender,
    handleCloseProviderModal,
    handleCloseConfirmDeleteModal,
    modalState,
  } = useProviderActions()

  const getDisplayTab = (name: Tabs): React.ReactElement => {
    switch (name) {
      case Tabs.DETAILS:
        return <Details provider={provider} />
      case Tabs.HISTORY:
        return <History provider={provider} />
      case Tabs.WORK_SHIFT:
        return <WorkShift provider={provider} />
      default:
        return <div></div>
    }
  }

  return (
    <>
      <div
        className={`provider-side-panel h-screen flex flex-col bg-white side-panel ${isHidden ? '' : 'open'}`}
        style={{
          width: isHidden ? '0' : '311px',
          border: isHidden ? 'none' : '1px solid var(--blue-200)',
          minWidth: isHidden ? '0' : '311px',
          margin: isHidden ? 0 : undefined,
          overflow: isHidden ? 'hidden' : undefined,
        }}
      >
        <div
          className={'flex-none personal-details'}
          style={{
            display: isHidden ? 'none' : '',
            width: isHidden ? '0' : '311px',
          }}
        >
          <div className="provider-buttons">
            <div className="profile-picture">
              <Avatar
                source={
                  provider?.photo_file?.file_path ??
                  provider?.photo ??
                  defaultAvatar
                }
                alt={provider ? getFullName(provider, true) : 'Staff'}
                height={64}
                width={64}
              />
              <button
                type="button"
                className="unstyled-button profile-picture-overlay"
                onClick={() => setShowEditPhotoModal(true)}
                aria-label="Edit photo"
              >
                <span className="overlay-content">
                  <i className="mgc_pencil_fill" aria-hidden></i>
                  <span>Edit photo</span>
                </span>
              </button>
            </div>
            <div style={{ width: '100%', marginLeft: '8px' }}>
              <div className="name-and-buttons">
                <div className="name d-flex flex-row flex-wrap">
                  <span className="provider-name">
                    {getFullName(provider, true)}
                  </span>
                </div>
                <div className="provider-sidepanel-header-actions">
                  <button
                    type="button"
                    className="provider-sidepanel-header-action"
                    aria-label="Close staff side panel"
                    title="Close"
                    onClick={toggle}
                  >
                    <PanelCloseIcon aria-hidden />
                  </button>
                </div>
              </div>
              {provider?.work_role && (
                <div className="provider-role">{provider.work_role}</div>
              )}
              {provider?.employment_type && (
                <div>
                  <span
                    className={`employment-type ${
                      provider?.employment_type === EmploymentStatus.FULL_TIME
                        ? 'full-time'
                        : 'part-time'
                    }`}
                  >
                    {provider?.employment_type}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="provider-action-buttons">
            <div className="icon-tray">
              <i className="mgc_calendar_time_add_fill"></i>
            </div>
            <button
              type="button"
              className="unstyled-button icon-tray"
              aria-label="Call"
              onClick={() => provider && handleCallClick(provider)}
            >
              <i className="ti ti-phone-filled" aria-hidden></i>
            </button>
            <button
              type="button"
              className="unstyled-button icon-tray"
              aria-label="Email"
              onClick={() => provider && handleEmailClick(provider)}
            >
              <i className="ti ti-mail-filled" aria-hidden></i>
            </button>
            <button
              type="button"
              className="unstyled-button icon-tray"
              aria-label="Message"
              onClick={() => provider && handleMessageClick(provider)}
            >
              <i className="ti ti-message-filled" aria-hidden></i>
            </button>
          </div>
        </div>
        <div className="provider-content-outer">
          <div className="provider-content-inner">
            <div
              className="flex-none provider-tabs"
              style={{ display: isHidden ? 'none' : '' }}
            >
              <ButtonGroup className="provider-btn m-0">
                {listofTabs.map((tab) => (
                  <Btn
                    variant="secondary"
                    onClick={() => setSelectedTab(tab.name)}
                    key={tab.name}
                    className={`${tab.name === selectedTab ? 'provider-btn-item' : ''}`}
                  >
                    <Tooltip content={tab.tooltip} placement="bottom" portal>
                      <i
                        className={`${
                          tab.name === selectedTab
                            ? `${tab.selected} selected`
                            : `${tab.unselected} unselected`
                        }`}
                        aria-label={tab.tooltip}
                      ></i>
                    </Tooltip>
                  </Btn>
                ))}
              </ButtonGroup>
            </div>
            <div className="provider-content-container">
              <div
                className="provider-content"
                style={{ display: isHidden ? 'none' : '' }}
                key={provider?.provider_id}
              >
                {getDisplayTab(selectedTab)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <EmailSender
        show={modalState.showEmailSender}
        onHide={handleCloseEmailSender}
        recipientEmail={modalState.selectedProviderContact?.email ?? ''}
      />
      {modalState.showCallModal && modalState.selectedProviderContact && (
        <SelectProviderContactModal
          show={modalState.showCallModal}
          onHide={handleCloseCallModal}
          providerData={modalState.selectedProviderContact}
        />
      )}
      {modalState.showProviderModal && (
        <AddProviderModal
          isOpen={modalState.showProviderModal}
          onRequestClose={handleCloseProviderModal}
          providerData={
            modalState.selectedProvider
              ? mapDatabaseToProviderData(modalState.selectedProvider)
              : undefined
          }
        />
      )}
      <ConfirmDeleteModal
        show={modalState.showConfirmDeleteModal}
        onHide={handleCloseConfirmDeleteModal}
        onConfirm={() =>
          modalState.providerToDelete &&
          confirmDeleteProvider(modalState.providerToDelete)
        }
        entityName={
          modalState.providerToDelete
            ? getFullName(modalState.providerToDelete)
            : ''
        }
      />
      {showEditPhotoModal && provider?.provider_id && (
        <EditPhoto
          show={showEditPhotoModal}
          onHide={() => setShowEditPhotoModal(false)}
          entityType="provider"
          entityId={provider.provider_id}
        />
      )}
    </>
  )
}

export default ProviderSidePanel
